import { inject, Injectable } from '@angular/core';

import { combineLatest } from 'rxjs';
import { last, takeUntil } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { AccountSelectors } from '@core/account-data';
import {
  BulkActionsController,
  BulkActionType,
  type BulkItemBase,
  ActionsVisibilityBreakpoint,
  BulkAction,
} from '@core/bulk-actions-legacy';
import type { OfferItem } from '@core/offers-types';
import { ReferralsService } from '@core/referrals-legacy';
import { type BulkItemWrapped, isBulkItemWrapped } from '@core/views-types';

import { ViewsOffersBulkActionsExecutorService } from './views-offers-bulk-actions-executor.service';
import { ViewsOffersBulkActionsValidatorService } from './views-offers-bulk-actions-validator.service';

@Injectable()
export class ViewsOffersBulkActionsController extends BulkActionsController {
  private readonly referralsService = inject(ReferralsService);
  private readonly executor = inject(ViewsOffersBulkActionsExecutorService);
  private readonly validator = inject(ViewsOffersBulkActionsValidatorService);

  protected availableActions: BulkAction[] = [
    {
      id: BulkActionType.Follow,
      buttonLabel: this.i18n.translate('app.bulk_actions.follow'),
      buttonIcon: 'bookmark',
      analytics: '[Offers] Toolbar_BulkActionsFollowButtonClick',
    },
    {
      id: BulkActionType.Unfollow,
      buttonLabel: this.i18n.translate('app.bulk_actions.unfollow'),
      buttonIcon: 'bookmark-border',
      analytics: '[Offers] Toolbar_BulkActionsUnfollowButtonClick',
    },
    {
      id: BulkActionType.ManageEeo,
      buttonLabel: this.i18n.translate('app.bulk_actions.manage_eeo'),
      buttonIcon: 'non-federal-company',
      analytics: '[Offers] Toolbar_BulkActionsManageEEOButtonClick',
    },
    {
      id: BulkActionType.ManageReferrals,
      buttonLabel: this.i18n.translate('app.bulk_actions.manage_referrals'),
      buttonIcon: 'favorite',
      analytics: '[Offers] Toolbar_BulkActionsManageReferralsButtonClick',
    },
    {
      id: BulkActionType.RetrieveArchived,
      buttonLabel: this.i18n.translate('app.bulk_actions.retrieve_archived_jobs'),
      buttonIcon: 'unarchive',
      analytics: '[Offers] Toolbar_BulkActionsRetrieveArchivedButtonClick',
    },
    {
      id: BulkActionType.DeleteArchived,
      buttonLabel: this.i18n.translate('app.bulk_actions.delete'),
      buttonIcon: 'delete',
      analytics: '[Offers] Toolbar_BulkActionsDeleteArchivedButtonClick',
    },
  ];

  private eeoEnabled: boolean;
  private canManageJobsReferralSettings: boolean;
  private canManageJobs: boolean;

  constructor() {
    super();

    combineLatest([
      this.store.select(AbilitiesSelectors.abilityMap),
      this.store.select(AccountSelectors.isEEOEnabled),
    ])
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(([abilitiesMap, eeoEnabled]) => {
        this.canManageJobsReferralSettings = abilitiesMap[Ability.ManageJobsReferralSettings];
        this.canManageJobs = abilitiesMap[Ability.ManageJobs];
        this.eeoEnabled = eeoEnabled;
      });
  }

  public getBulkActions(): BulkAction[] {
    const active = this.getActionsForActiveJobs();
    const selectedOffers = <OfferItem[]>this.getSelectedOffers();
    const hasAllOffersActive = selectedOffers.every(offer => offer.status !== 'archived');
    if (hasAllOffersActive) return active;

    const hasAllOffersArchived = selectedOffers.every(offer => offer.status === 'archived');
    const archived = this.getActionsForArchivedJobs();
    if (hasAllOffersArchived) return archived;

    return [
      ...active,
      ...archived.filter(action => !active.find(activeAction => activeAction.id === action.id)),
    ];
  }

  public dispatchAction(data: { action: BulkAction }): void {
    const { action } = data;
    const offers = this.getSelectedOffers();
    const ids = offers.map(c => c.id);

    if (action.analytics) {
      typeof action.analytics === 'string'
        ? this.analytics.track(action.analytics)
        : action.analytics();
    }

    const actionPermitted = this.validator.isActionPermitted(action.id, ids);
    if (!actionPermitted) return;

    const bulkActionExecution$ = this.executor.getExecutionStream(action, ids);
    bulkActionExecution$.pipe(last()).subscribe({
      next: _ => {
        if (_) {
          // Action may be cancelled before was sent to BE. If we get truthy payload here,
          //  it means action was dispatched
          this.markBulkActionAsCompleted(action);
          this.clearSelection();
        }
      },
      error: error => {
        this.markBulkActionAsCompleted(action);
        this.dispatchActionError(action, error);
      },
    });
  }

  public getActionsVisibilityByBreakpoint(): ActionsVisibilityBreakpoint[] {
    return [
      {
        min: 0,
        max: Number.POSITIVE_INFINITY,
        actionIds: [
          BulkActionType.Follow,
          BulkActionType.Unfollow,
          BulkActionType.RetrieveArchived,
          BulkActionType.DeleteArchived,
        ],
      },
    ];
  }

  protected getSelectedOffers(): BulkItemBase[] {
    const items = <Array<BulkItemWrapped | BulkItemBase>>this.selectedItems$.getValue();
    const allItems = items.map(item => {
      if (isBulkItemWrapped in item) return item.record;

      return item;
    });
    return Object.values(Object.fromEntries(allItems.map(item => [item.id, item])));
  }

  private getActionsForActiveJobs(): BulkAction[] {
    const referralsEnabled = this.referralsService.isProgramEnabled();
    return this.availableActions.filter(action => {
      switch (action.id) {
        case BulkActionType.Follow:
        case BulkActionType.Unfollow:
          return true;

        case BulkActionType.ManageEeo:
          return this.eeoEnabled && this.canManageJobs;

        case BulkActionType.ManageReferrals:
          return referralsEnabled && this.canManageJobsReferralSettings;

        default:
          return false;
      }
    });
  }

  private getActionsForArchivedJobs(): BulkAction[] {
    return this.availableActions.filter(action => {
      switch (action.id) {
        case BulkActionType.DeleteArchived:
        case BulkActionType.RetrieveArchived:
          return this.canManageJobs;
        default:
          return false;
      }
    });
  }
}
